// Rapport-auf-Klick (TQ #1151): lokaler Merker "welcher Auftrag wurde zuletzt ausgestempelt" und
// Fehlertexte. Bewusst getrennt von personalOutbox/db: der Stempel-Pfad bleibt unberuehrt, der
// Merker ist reine Anzeige-Hilfe (localStorage, pro Mitarbeiter) und darf fehlen.

export interface RapportAuftrag {
  auftragId: string
  auftragsnummer: string
  auftraggeberName: string
}

export interface RapportAngebot extends RapportAuftrag {
  /** Zeitpunkt des Ausstempelns (ISO). */
  ausgestempeltAm: string
  /** Gesetzt, sobald der Rapport erzeugt wurde. */
  rapport?: { dateiname: string; pfad: string; link: string | null }
}

interface Speicher {
  letzteEinstempelung?: RapportAuftrag
  angebot?: RapportAngebot
}

const key = (employeeId: string) => `stempel.rapport.v1.${employeeId}`

/** Kalendertag (lokale Zeit) als YYYY-MM-DD. */
function kalendertag(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Ein Angebot gilt nur am Kalendertag des Ausstempelns; danach (oder bei ungueltigem Datum) verfaellt es. */
export function angebotGueltig(a: RapportAngebot, jetzt = new Date()): boolean {
  const t = new Date(a.ausgestempeltAm)
  return !Number.isNaN(t.getTime()) && kalendertag(t) === kalendertag(jetzt)
}

export function ladeRapportSpeicher(employeeId: string, jetzt = new Date()): Speicher {
  try {
    const raw = localStorage.getItem(key(employeeId))
    const s = raw ? (JSON.parse(raw) as Speicher) : {}
    if (s.angebot && !angebotGueltig(s.angebot, jetzt)) {
      const { angebot: _verworfen, ...rest } = s
      return rest
    }
    return s
  } catch {
    return {}
  }
}

/** Nur http(s)-Links werden als href verwendet (kein javascript:/data: aus Server- oder Speicherdaten). */
export function sichererLink(link: string | null | undefined): string | null {
  if (!link) return null
  try {
    const u = new URL(link)
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.href : null
  } catch {
    return null
  }
}

function speichere(employeeId: string, s: Speicher) {
  try {
    localStorage.setItem(key(employeeId), JSON.stringify(s))
  } catch {
    // Speicher gesperrt/voll: der Link entfaellt dann nach einem Neuladen, nichts Schlimmeres.
  }
}

/** Nach erfasstem Kommen: Auftrag merken, ein altes Angebot verfaellt (neuer Arbeitstag/Einsatz). */
export function merkeEinstempeln(employeeId: string, a: RapportAuftrag | null) {
  speichere(employeeId, a ? { letzteEinstempelung: a } : {})
}

/** Nach erfasstem Gehen: Angebot fuer den zuletzt eingestempelten Auftrag anlegen.
 * Gibt es keinen gemerkten Auftrag (Kommen auf anderem Geraet), entsteht kein Angebot. */
export function merkeAusstempeln(employeeId: string, jetztIso = new Date().toISOString()): RapportAngebot | null {
  const s = ladeRapportSpeicher(employeeId)
  if (!s.letzteEinstempelung) return null
  const angebot: RapportAngebot = { ...s.letzteEinstempelung, ausgestempeltAm: jetztIso }
  speichere(employeeId, { ...s, angebot })
  return angebot
}

export function merkeRapport(employeeId: string, rapport: NonNullable<RapportAngebot['rapport']>): RapportAngebot | null {
  const s = ladeRapportSpeicher(employeeId)
  if (!s.angebot) return null
  const angebot = { ...s.angebot, rapport }
  speichere(employeeId, { ...s, angebot })
  return angebot
}

/** Verstaendlicher deutscher Text nach Status; nie Systemtext oder HTTP-Code. */
export function rapportFehlerText(e: unknown): string {
  if (e instanceof Error && e.name === 'SessionExpiredError') {
    return 'Die Anmeldung ist abgelaufen. Bitte neu anmelden.'
  }
  const status = (e as { status?: number } | null)?.status
  if (status === undefined) return 'Keine Verbindung. Bitte mit Internet erneut versuchen.'
  switch (status) {
    case 409:
      return 'Für diesen Auftrag gibt es heute noch kein Ausstempeln mit Einträgen. Bitte zuerst ausstempeln.'
    case 404:
      return 'Dieser Auftrag wurde nicht gefunden.'
    case 400:
    case 413:
      return 'Die Unterschrift ist ungültig oder zu groß. Bitte erneut unterschreiben oder überspringen.'
    case 503:
      return 'Die Nextcloud-Ablage für diesen Betrieb ist nicht eingerichtet. Bitte im Büro melden.'
    case 502:
      return 'Die Nextcloud ist gerade nicht erreichbar. Bitte später noch einmal versuchen.'
    default:
      return 'Der Rapport konnte nicht erstellt werden. Bitte später noch einmal versuchen.'
  }
}
